import { useState } from "react";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Bot,
  TrendingUp,
  MessageSquare,
  PenTool,
  Cpu,
  Target,
  Users,
  CalendarDays,
  CheckCircle2,
  Gift,
  PlayCircle,
  BrainCircuit,
  Image as ImageIcon,
  MessageCircle,
  Clock
} from "lucide-react";

const AIForBusiness = () => {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <Navbar />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 text-center max-w-4xl mb-16 mt-8">
          <Badge className="mb-6 bg-blue-100 text-blue-700 hover:bg-blue-200 border-none px-4 py-1.5 text-sm font-medium">
            <CalendarDays className="w-4 h-4 mr-2 inline" />
            Private Class 2 วันเต็ม (16 ชั่วโมง)
          </Badge>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-slate-900">
            คอร์ส Private สอนจับมือทำ <br className="hidden md:block"/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              ใช้ AI ขับเคลื่อนธุรกิจครบทุกมิติ
            </span>
          </h1>
          
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            น้อง Hermes เลขา AI · ยิงแอด · Content ล้านวิว · โฆษณาสวยๆ · Automation · วิเคราะห์ยอดขาย
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Button size="lg" className="w-full sm:w-auto text-lg px-8 h-14 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30 rounded-xl" onClick={() => window.open('https://m.me/sanookai', '_blank')}>
              <MessageCircle className="w-5 h-5 mr-2" />
              สอบถาม / สมัครเรียน
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 h-14 border-slate-300 text-slate-700 hover:bg-slate-100 rounded-xl" onClick={() => {
              document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' });
            }}>
              ดูเนื้อหาคอร์ส
            </Button>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="container mx-auto px-4 max-w-6xl mb-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">ผู้ช่วย AI ที่ทำงานให้คุณ 24 ชม.</h2>
            <p className="text-slate-600 text-lg">สรุปธุรกิจ วิเคราะห์ยอด Push Telegram ทุกวัน</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <TrendingUp className="text-blue-500 w-6 h-6" />, title: "สรุปภาพรวมกิจการ", desc: "รายงานยอดขาย กำไร สต็อก ให้เจ้าของทุกเช้า" },
              { icon: <MessageSquare className="text-indigo-500 w-6 h-6" />, title: "Push Telegram ทุกวัน", desc: "ส่งสรุปข่าว ยอดขาย แจ้งเตือนสำคัญผ่าน Telegram" },
              { icon: <Target className="text-blue-500 w-6 h-6" />, title: "วิเคราะห์ยอดขาย", desc: "เปรียบเทียบยอด แนะนำกลยุทธ์ พยากรณ์ Trend" },
              { icon: <Bot className="text-indigo-500 w-6 h-6" />, title: "ตอบแชทลูกค้า", desc: "ตอบคำถามลูกค้า 24 ชม. อัจฉริยะเหมือนคนจริง" },
              { icon: <PenTool className="text-blue-500 w-6 h-6" />, title: "สร้าง Content", desc: "เขียนโพสต์ แคปชั่น สคริปต์ ให้อัตโนมัติ" },
              { icon: <Cpu className="text-indigo-500 w-6 h-6" />, title: "Automation", desc: "ทำงานซ้ำๆ แทนคุณ ลดเวลา เพิ่มประสิทธิภาพ" },
            ].map((feature, idx) => (
              <Card key={idx} className="bg-white border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 border border-blue-100">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-slate-800">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Highlight Banner */}
        <section className="container mx-auto px-4 max-w-5xl mb-24">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-white shadow-2xl flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-4">เรียนตรงจุด จับมือทำจริง</h2>
              <p className="text-blue-100 text-lg mb-6 leading-relaxed">
                เน้นใช้ AI จริงในธุรกิจ ไม่ใช่ทฤษฎี ทำโปรเจกต์จริงของธุรกิจคุณ สอนตัวต่อตัวผ่าน Google Meet และคุณสามารถกำหนดวันเรียนเองได้ตามสะดวก
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-blue-300" />
                  <span className="font-medium">Private Class</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-blue-300" />
                  <span className="font-medium">เลือกวันเรียนได้</span>
                </div>
              </div>
            </div>
            <div className="w-full md:w-auto flex-shrink-0">
               <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 text-center">
                 <div className="text-5xl font-black mb-2 tracking-tight">16 ชม.</div>
                 <div className="text-blue-200 font-medium">เรียนเต็มอิ่ม 2 วัน</div>
               </div>
            </div>
          </div>
        </section>

        {/* Curriculum Section */}
        <section id="curriculum" className="container mx-auto px-4 max-w-4xl mb-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">เนื้อหา 16 ชั่วโมงเต็ม</h2>
            <p className="text-slate-600 text-lg">เจาะลึกทุกมิติของการประยุกต์ใช้ AI ในธุรกิจ</p>
          </div>

          <div className="space-y-6">
            {[
              {
                icon: <BrainCircuit className="w-6 h-6 text-blue-600" />,
                title: "รู้จัก AI ทุกค่าย",
                items: ["ChatGPT / Claude / Gemini — เลือกใช้ให้เหมาะกับงาน", "Prompt Engineering สำหรับผู้ประกอบการ", "ใช้ AI วิเคราะห์ธุรกิจ คู่แข่ง ตลาด"]
              },
              {
                icon: <Bot className="w-6 h-6 text-indigo-600" />,
                title: "น้อง Hermes — เลขา AI ฉลาดที่สุดในโลก",
                items: ["ติดตั้ง Hermes AI เลขาส่วนตัว 24 ชม.", "สรุปภาพรวมกิจการ ส่งรายงานทุกวัน", "Push สรุปข่าว + ยอดขายผ่าน Telegram อัตโนมัติ", "วิเคราะห์ยอดขาย แนะนำกลยุทธ์"]
              },
              {
                icon: <TrendingUp className="w-6 h-6 text-blue-600" />,
                title: "ทำเพจ & Content ล้านวิว",
                items: ["สร้าง Content ที่คนอยากแชร์ด้วย AI", "เขียนแคปชั่น สคริปต์วิดีโอ ด้วย AI", "ใช้ AI สร้างภาพ วิดีโอ สำหรับ Social Media", "กลยุทธ์ Content ที่ได้ Reach ล้านวิว"]
              },
              {
                icon: <ImageIcon className="w-6 h-6 text-indigo-600" />,
                title: "สร้างโฆษณาสวยๆ ด้วย AI",
                items: ["ออกแบบ Artwork โฆษณาระดับมืออาชีพ", "สร้าง Banner, Poster, Social Post ด้วย AI", "ไม่ต้องจ้างกราฟิก ทำเองได้เลย"]
              }
            ].map((module, idx) => (
              <Card key={idx} className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="shrink-0">
                      <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm">
                        {module.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-800 mb-4">{module.title}</h3>
                      <ul className="space-y-3">
                        {module.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-slate-600">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2.5 shrink-0"></div>
                            <span className="leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Pricing / CTA Section */}
        <section className="container mx-auto px-4 max-w-3xl mb-24">
          <Card className="bg-white border-blue-100 shadow-xl shadow-blue-900/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-50 rounded-full blur-3xl -ml-32 -mb-32 opacity-50 pointer-events-none"></div>
            
            <CardContent className="p-8 md:p-12 relative z-10 text-center">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">พร้อมให้ AI ขับเคลื่อนธุรกิจคุณ?</h2>
              <p className="text-slate-600 mb-8 text-lg font-medium">2 วัน · 16 ชั่วโมง · Private Google Meet · เลือกวันเรียนเองได้</p>
              
              <div className="text-left bg-slate-50 rounded-2xl p-6 mb-8 border border-slate-200 shadow-sm">
                <ul className="space-y-4">
                  {[
                    "สอนจับมือทำ Private ผ่าน Google Meet",
                    "น้อง Hermes เลขา AI + Push Telegram",
                    "ยิงแอด + Content ล้านวิว + โฆษณา AI",
                    "ระบบ Automation + วิเคราะห์ยอดขาย"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 font-medium text-slate-700">
                      <CheckCircle2 className="text-blue-500 shrink-0 w-5 h-5" />
                      {item}
                    </li>
                  ))}
                  <li className="flex items-center gap-3 font-bold text-indigo-700 pt-3 border-t border-slate-200">
                    <Gift className="text-indigo-500 shrink-0 w-5 h-5" />
                    🎁 ฟรี! Google Ultra 2 เดือน (มูลค่า 9,400 บาท)
                  </li>
                  <li className="flex items-center gap-3 font-bold text-indigo-700">
                    <Gift className="text-indigo-500 shrink-0 w-5 h-5" />
                    🎁 ฟรี! ChatGPT Plus 2 เดือน (มูลค่า 1,400 บาท)
                  </li>
                </ul>
              </div>

              <Button size="lg" className="w-full text-lg h-16 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-xl shadow-blue-500/25 rounded-xl transition-all hover:scale-[1.02]" onClick={() => window.open('https://m.me/sanookai', '_blank')}>
                🔥 สมัครเรียนเลย
              </Button>
              <p className="mt-4 text-slate-500 text-sm font-medium">ทักแชทเพื่อจองวันเรียนและรับสิทธิพิเศษ</p>
            </CardContent>
          </Card>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 max-w-3xl mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900">คำถามที่พบบ่อย (FAQ)</h2>
          </div>
          
          <div className="space-y-4">
            {[
              { q: "ไม่มีพื้นฐาน AI เลยเรียนได้ไหม?", a: "ได้เลยครับ! เราสอนตั้งแต่พื้นฐาน และปรับเนื้อหาตามระดับของผู้เรียนแต่ละท่าน" },
              { q: "เลือกวันเรียนยังไง?", a: "หลังชำระเงิน ทีมงานจะติดต่อเพื่อนัดวัน 2 วันที่คุณสะดวก ไม่จำเป็นต้องเรียนติดกันครับ" },
              { q: "Google Ultra กับ ChatGPT Plus ได้ยังไง?", a: "เราจะทำการเปิดระบบให้คุณทันทีในวันแรกที่เรียน และสามารถใช้งานได้เต็มๆ 2 เดือน" },
              { q: "ชำระเงินยังไง?", a: "ทักแชท m.me/sanookai ทีมงานจะส่งช่องทางการชำระเงินและรายละเอียดให้ครับ" }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <h4 className="text-lg font-bold text-slate-800 mb-3">{faq.q}</h4>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default AIForBusiness;
